import React, { useState, useEffect } from "react";
import {
  Box,
  FormHelperText,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TodayIcon from "@mui/icons-material/Today";
import TimerIcon from "@mui/icons-material/Timer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";

// Common timezones list
const COMMON_TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US & Canada)" },
  { value: "America/Denver", label: "Mountain Time (US & Canada)" },
  { value: "America/Chicago", label: "Central Time (US & Canada)" },
  { value: "America/New_York", label: "Eastern Time (US & Canada)" },
  { value: "Europe/London", label: "London, Edinburgh (GMT)" },
  { value: "Europe/Paris", label: "Paris, Berlin, Rome (CET)" },
  { value: "Asia/Tokyo", label: "Tokyo, Seoul" },
  { value: "Australia/Sydney", label: "Sydney, Melbourne" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
  { value: "Pacific/Auckland", label: "Auckland, Wellington" },
  { value: "Asia/Bangkok", label: "Bangkok, Jakarta" },
  { value: "Asia/Dubai", label: "Dubai, Abu Dhabi" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "America/Cancun", label: "Cancun, Cozumel" },
  { value: "America/Nassau", label: "Bahamas" },
  { value: "America/Costa_Rica", label: "Costa Rica" },
  { value: "America/Belize", label: "Belize" },
  { value: "Asia/Maldives", label: "Maldives" },
  { value: "Asia/Kuala_Lumpur", label: "Malaysia" },
  { value: "Asia/Manila", label: "Philippines" },
  { value: "Pacific/Fiji", label: "Fiji" },
  { value: "Pacific/Tahiti", label: "Tahiti" },
  { value: "Indian/Maldives", label: "Maldives" },
];

const CompactDiveDateTime = ({
  diveDate,
  setDiveDate,
  diveStartTime,
  setDiveStartTime,
  diveDuration,
  setDiveDuration,
  diveTimeZone,
  setDiveTimeZone,
  disabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Local state for date and time
  const [dateTimeValue, setDateTimeValue] = useState(null);
  const [durationValues, setDurationValues] = useState({
    hours: 0,
    minutes: 0,
  });
  const [timeZoneInput, setTimeZoneInput] = useState("");

  // Initialize component state from props
  useEffect(() => {
    // Set date and time if provided
    if (diveDate && diveStartTime) {
      const dateObj = new Date(diveStartTime);
      setDateTimeValue(dateObj);
    }

    // Set duration if provided
    if (diveDuration) {
      const [hours, minutes] = diveDuration
        .split(":")
        .map((val) => parseInt(val, 10));
      setDurationValues({ hours, minutes });
    }

    // Set timezone if not already set
    if (!diveTimeZone) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDiveTimeZone(userTimeZone);
      const tzObject = COMMON_TIMEZONES.find(
        (tz) => tz.value === userTimeZone
      ) || { label: userTimeZone };
      setTimeZoneInput(tzObject.label);
    } else {
      const tzObject = COMMON_TIMEZONES.find(
        (tz) => tz.value === diveTimeZone
      ) || { label: diveTimeZone };
      setTimeZoneInput(tzObject.label);
    }
  }, [diveDate, diveStartTime, diveDuration, diveTimeZone, setDiveTimeZone]);

  // Handle date change
  const handleDateChange = (newDate) => {
    // Update the local state
    setDateTimeValue((prev) => {
      // If we already have a datetime, preserve the time portion
      if (prev) {
        newDate.setHours(
          prev.getHours(),
          prev.getMinutes(),
          prev.getSeconds(),
          prev.getMilliseconds()
        );
      }
      return newDate;
    });

    // Update parent component state
    if (newDate) {
      const dateString = newDate.toISOString().split("T")[0];
      setDiveDate(dateString);

      // If we have time info, update the start time as well
      if (dateTimeValue) {
        const updatedDateTime = new Date(newDate);
        updatedDateTime.setHours(
          dateTimeValue.getHours(),
          dateTimeValue.getMinutes(),
          0,
          0
        );
        setDiveStartTime(updatedDateTime.toISOString());
      }
    }
  };

  // Handle time change
  const handleTimeChange = (newTime) => {
    // Update the local state
    setDateTimeValue((prev) => {
      let updatedTime;
      if (prev) {
        // If we already have a datetime, preserve the date portion
        updatedTime = new Date(prev);
        updatedTime.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      } else {
        // If no date was selected yet, use today
        updatedTime = new Date();
        updatedTime.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
        const dateString = updatedTime.toISOString().split("T")[0];
        setDiveDate(dateString);
      }
      return updatedTime;
    });

    // Update parent component state
    if (newTime) {
      const updatedDateTime = new Date(diveDate || new Date());
      updatedDateTime.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
      setDiveStartTime(updatedDateTime.toISOString());
    }
  };

  // Handle duration change
  const handleDurationChange = (type, value) => {
    const numValue = parseInt(value, 10) || 0;
    const updatedValues = {
      ...durationValues,
      [type]: numValue,
    };
    setDurationValues(updatedValues);

    // Update parent component state
    const formattedHours = updatedValues.hours.toString().padStart(2, "0");
    const formattedMinutes = updatedValues.minutes.toString().padStart(2, "0");
    setDiveDuration(`${formattedHours}:${formattedMinutes}`);
  };

  // Handle timezone selection
  const handleTimezoneChange = (event, newValue) => {
    if (newValue) {
      setDiveTimeZone(newValue.value);
      setTimeZoneInput(newValue.label);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Date Picker */}
          <Grid item xs={12} sm={12}>
            <DatePicker
              label="Dive Date"
              value={dateTimeValue}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  disabled: disabled,
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <TodayIcon color="primary" />
                      </InputAdornment>
                    ),
                  },
                },
              }}
            />
          </Grid>

          {/* Time Picker and Timezone on the same row */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Start Time"
                  value={dateTimeValue}
                  onChange={handleTimeChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      disabled: disabled,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccessTimeIcon color="primary" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                  ampm
                  views={["hours", "minutes"]}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  disablePortal
                  options={COMMON_TIMEZONES}
                  value={
                    COMMON_TIMEZONES.find((tz) => tz.value === diveTimeZone) ||
                    null
                  }
                  getOptionLabel={(option) => option.label}
                  inputValue={timeZoneInput}
                  onInputChange={(event, newInputValue) => {
                    setTimeZoneInput(newInputValue);
                  }}
                  onChange={handleTimezoneChange}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Timezone"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationOnIcon color="primary" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            <Tooltip title="Select the timezone where the dive took place">
                              <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <FormHelperText>
                  Select the timezone where the dive took place
                </FormHelperText>
              </Grid>
            </Grid>
          </Grid>

          {/* Duration Fields on their own row */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Hours"
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TimerIcon color="primary" />
                      </InputAdornment>
                    ),
                    inputProps: { min: 0, max: 24 },
                  }}
                  value={durationValues.hours}
                  onChange={(e) =>
                    handleDurationChange("hours", e.target.value)
                  }
                  disabled={disabled}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Minutes"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0, max: 59 },
                  }}
                  value={durationValues.minutes}
                  onChange={(e) =>
                    handleDurationChange("minutes", e.target.value)
                  }
                  disabled={disabled}
                />
              </Grid>
            </Grid>
            <FormHelperText>
                  Dive Duration in hours and minutes
                </FormHelperText>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default CompactDiveDateTime;
